// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/pages/sales/settings/ProgramForm.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Trash2, Loader2, Save, Layers, 
  IndianRupee, UploadCloud, X, Globe, Image as ImageIcon 
} from 'lucide-react';
import { toast } from 'react-toastify';
import SummaryApi from '../../../../common/SummaryApi';
import api from '../../../../common/apiClient';
import SelectDropdown from '../../../../customStyles/SelectDropdown';

// Helper to calculate live discount and tax for each plan
const computePlanFinancials = (plan) => {
  const original = parseFloat(plan.originalPrice) || 0;
  const discountVal = parseFloat(plan.discountValue) || 0;
  let discountAmt = 0;

  if (plan.discountType === 'percentage') {
    discountAmt = (original * discountVal) / 100;
  } else if (plan.discountType === 'flat') {
    discountAmt = discountVal;
  }

  const sellingPrice = Math.max(0, Math.round(original - discountAmt));

  const gst = parseFloat(plan.gstRate) || 0;
  let taxAmount = 0;
  let totalAmount = 0;

  if (plan.isTaxInclusive) {
    totalAmount = Math.round(sellingPrice);
    const base = totalAmount / (1 + gst / 100);
    taxAmount = Math.round(totalAmount - base);
  } else {
    taxAmount = Math.round((sellingPrice * gst) / 100);
    totalAmount = Math.round(sellingPrice + taxAmount);
  }

  return {
    sellingPrice,
    taxAmount,
    totalAmount,
  };
};

const ProgramForm = () => {
  const { portalName, id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  // Loading States
  const [pageLoading, setPageLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Dropdown References
  const [divisions, setDivisions] = useState([]);
  const [products, setProducts] = useState([]);

  // File & Image States
  const [existingImages, setExistingImages] = useState([]); // [{ _id, url, publicId }]
  const [removedPublicIds, setRemovedPublicIds] = useState([]); // Track deleted publicIds for Cloudinary
  const [newFiles, setNewFiles] = useState([]); // New File objects to upload
  const fileInputRef = useRef(null);

  // Form State
  const [formData, setFormData] = useState({
    divisionId: '',
    ourProductId: '',
    name: '',
    code: '',
    description: '',
    websiteRedirectUrl: '', //  Added: Public website link
    offeringType: 'sports_program',
    currency: 'INR',
    duration: { value: 0, unit: 'na' },
    totalSessions: 0,
    sku: '',
    stockQuantity: 0,
    isActive: true,
    pricingPlans: [
      {
        planName: 'Standard',
        billingCycle: 'one_time',
        originalPrice: 0,
        discountType: 'none',
        discountValue: 0,
        isTaxInclusive: false,
        gstRate: 18,
      },
    ],
    customFields: [],
  });

  const [formErrors, setFormErrors] = useState({});

  // 1. Fetch Divisions and Products
  useEffect(() => {
    const fetchReferences = async () => {
      try {
        const [divRes, prodRes] = await Promise.all([
          api.get(SummaryApi.getAllDivisions.url),
          api.get(SummaryApi.getAllProducts.url),
        ]);
        if (divRes.data?.success) setDivisions(divRes.data.data || []);
        if (prodRes.data?.success) setProducts(prodRes.data.data || []);
      } catch (err) {
        console.error('Error fetching dropdown references:', err);
      }
    };
    fetchReferences();
  }, []);

  // 2. Fetch Existing Data for Edit Mode
  useEffect(() => {
    if (!isEditMode) return;

    const fetchProgram = async () => {
      setPageLoading(true);
      try {
        const url = typeof SummaryApi.getProgramById.url === 'function'
          ? SummaryApi.getProgramById.url(id)
          : SummaryApi.getProgramById.url;

        const response = await api.get(url);
        if (response.data?.success) {
          const doc = response.data.data;
          setFormData({
            divisionId: doc.divisionId?._id || doc.divisionId || '',
            ourProductId: doc.ourProductId?._id || doc.ourProductId || '',
            name: doc.name || '',
            code: doc.code || '',
            description: doc.description || '',
            websiteRedirectUrl: doc.websiteRedirectUrl || '',
            offeringType: doc.offeringType || 'sports_program',
            currency: doc.currency || 'INR',
            duration: doc.duration || { value: 0, unit: 'na' },
            totalSessions: doc.totalSessions || 0,
            sku: doc.sku || '',
            stockQuantity: doc.stockQuantity || 0,
            isActive: doc.isActive !== undefined ? doc.isActive : true,
            pricingPlans: doc.pricingPlans?.length > 0 ? doc.pricingPlans : [],
            customFields: doc.customFields || [],
          });
          // Set existing images with their publicId
          setExistingImages(doc.images || []);
        }
      } catch (error) {
        console.error('Error loading program:', error);
        toast.error('Failed to load offering details');
        navigate(`/${portalName}/sales/settings/programs`);
      } finally {
        setPageLoading(false);
      }
    };

    fetchProgram();
  }, [id, isEditMode, navigate, portalName]);

  // Image Selection Handler
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (existingImages.length + newFiles.length + files.length > 5) {
      toast.warning('Maximum 5 images allowed');
      return;
    }
    setNewFiles((prev) => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Remove Existing Image (Track publicId for Cloudinary deletion)
  const handleRemoveExistingImage = (imageObj) => {
    setExistingImages((prev) => prev.filter((img) => img.publicId !== imageObj.publicId));
    if (imageObj.publicId) {
      setRemovedPublicIds((prev) => [...prev, imageObj.publicId]);
    }
  };

  // Remove New Local File
  const handleRemoveNewFile = (index) => {
    setNewFiles((prev) => prev.filter((_, idx) => idx !== index));
  };

  // Pricing Plan Handlers
  const handleAddPricingPlan = () => {
    setFormData((prev) => ({
      ...prev,
      pricingPlans: [
        ...prev.pricingPlans,
        {
          planName: 'New Plan',
          billingCycle: 'monthly',
          originalPrice: 0,
          discountType: 'none',
          discountValue: 0,
          isTaxInclusive: false,
          gstRate: 18,
        },
      ],
    }));
  };

  const handleRemovePricingPlan = (index) => {
    if (formData.pricingPlans.length === 1) {
      toast.warning('At least one pricing plan is required');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      pricingPlans: prev.pricingPlans.filter((_, idx) => idx !== index),
    }));
  };

  const handlePlanChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.pricingPlans];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, pricingPlans: updated };
    });
  };

  // Custom Field Handlers
  const handleAddCustomField = () => {
    setFormData((prev) => ({
      ...prev,
      customFields: [...prev.customFields, { label: '', value: '', fieldType: 'text' }],
    }));
  };

  const handleRemoveCustomField = (index) => {
    setFormData((prev) => ({
      ...prev,
      customFields: prev.customFields.filter((_, idx) => idx !== index),
    }));
  };

  const handleCustomFieldChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.customFields];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, customFields: updated };
    });
  };

  // Form Validation
  const validateForm = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Offering name is required';
    if (!formData.divisionId) errs.divisionId = 'Please select a Division';
    if (!formData.offeringType) errs.offeringType = 'Please select Offering Type';

    if (!formData.pricingPlans || formData.pricingPlans.length === 0) {
      errs.pricingPlans = 'At least one pricing plan is mandatory';
    } else {
      formData.pricingPlans.forEach((plan, idx) => {
        if (!plan.planName?.trim()) errs[`planName_${idx}`] = 'Plan name required';
        if (plan.originalPrice < 0 || isNaN(plan.originalPrice)) {
          errs[`originalPrice_${idx}`] = 'Valid price required';
        }
      });
    }

    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Submit Handler using FormData for Multer & Cloudinary
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setSubmitLoading(true);
    try {
      const formPayload = new FormData();
      formPayload.append('divisionId', formData.divisionId);
      if (formData.ourProductId) formPayload.append('ourProductId', formData.ourProductId);
      formPayload.append('name', formData.name.trim());
      formPayload.append('code', formData.code.trim().toUpperCase());
      formPayload.append('description', formData.description.trim());
      formPayload.append('websiteRedirectUrl', formData.websiteRedirectUrl.trim());
      formPayload.append('offeringType', formData.offeringType);
      formPayload.append('currency', formData.currency);
      formPayload.append('sku', formData.sku);
      formPayload.append('stockQuantity', formData.stockQuantity);
      formPayload.append('totalSessions', formData.totalSessions);
      formPayload.append('isActive', formData.isActive);

      // Stringify complex nested JSON structures for Multer
      formPayload.append('pricingPlans', JSON.stringify(formData.pricingPlans));
      formPayload.append('duration', JSON.stringify(formData.duration));
      formPayload.append('customFields', JSON.stringify(formData.customFields));

      // Append removed Cloudinary publicIds in edit mode
      if (isEditMode) {
        formPayload.append('removedPublicIds', JSON.stringify(removedPublicIds));
      }

      // Append newly selected files
      newFiles.forEach((file) => {
        formPayload.append('images', file);
      });

      let response;
      if (isEditMode) {
        const url = typeof SummaryApi.updateProgram.url === 'function'
          ? SummaryApi.updateProgram.url(id)
          : SummaryApi.updateProgram.url;
        response = await api.put(url, formPayload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        response = await api.post(SummaryApi.createProgram.url, formPayload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      if (response.data?.success) {
        toast.success(isEditMode ? 'Offering updated successfully' : 'Offering created successfully');
        navigate(`/${portalName}/sales/settings/programs`);
      } else {
        toast.error(response.data?.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving program:', error);
      toast.error(error.response?.data?.message || 'An error occurred while saving');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-16">
        <Loader2 className="w-8 h-8 text-red-600 animate-spin mb-3" />
        <p className="text-sm text-gray-500">Loading offering details...</p>
      </div>
    );
  }

  return (
    <div className="p-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(`/${portalName}/sales/settings/programs`)}
            className="p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              {isEditMode ? 'Edit Offering / Program' : 'Create New Offering'}
            </h1>
            <p className="text-xs md:text-sm text-gray-500">
              Configure details, dynamic pricing plans, taxes, and division mappings
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Section 1: Basic Information */}
        <div className="bg-white p-5 md:p-6 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-base font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
            <Layers className="w-4 h-4 text-red-600" />
            Basic Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Division Select */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Division <span className="text-red-500">*</span>
              </label>
              <SelectDropdown
                options={divisions}
                value={formData.divisionId}
                onChange={(val) => setFormData((prev) => ({ ...prev, divisionId: val }))}
                placeholder="Select Division"
                valueKey="_id"
                labelKey="name"
                searchable={true}
              />
              {formErrors.divisionId && (
                <p className="text-red-500 text-xs mt-1">{formErrors.divisionId}</p>
              )}
            </div>

            {/* Offering Type */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Offering Type <span className="text-red-500">*</span>
              </label>
              <SelectDropdown
                options={[
                  { value: 'sports_program', label: 'Sports Program / Coaching' },
                  { value: 'course', label: 'Course (Long term)' },
                  { value: 'workshop', label: 'Workshop (Short term)' },
                  { value: 'subscription', label: 'Subscription (Software SaaS)' },
                  { value: 'physical_product', label: 'Physical Product (Goods / TV)' },
                ]}
                value={formData.offeringType}
                onChange={(val) => setFormData((prev) => ({ ...prev, offeringType: val }))}
                placeholder="Select Type"
                valueKey="value"
                labelKey="label"
                searchable={false}
              />
            </div>

            {/* Optional OurProduct Link */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Parent Product (Optional)
              </label>
              <SelectDropdown
                options={[{ _id: '', name: 'None' }, ...products]}
                value={formData.ourProductId}
                onChange={(val) => setFormData((prev) => ({ ...prev, ourProductId: val }))}
                placeholder="Map to ERP Product"
                valueKey="_id"
                labelKey="name"
                searchable={true}
              />
            </div>

            {/* Offering Name */}
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Offering / Program Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Standard Plan, Archery Beginners, Smart TV 55 Inch"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  formErrors.name ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {formErrors.name && (
                <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
              )}
            </div>

            {/* Code */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Code / SKU
              </label>
              <input
                type="text"
                placeholder="e.g. ARC-001 or TV-55"
                value={formData.code}
                onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 uppercase font-mono"
              />
            </div>

            {/*  Website Redirect URL */}
            <div className="md:col-span-3">
              <label className="text-xs font-semibold text-gray-700 uppercase mb-1 flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-blue-600" />
                Public Website Link / Landing Page URL
              </label>
              <input
                type="url"
                placeholder="https://yourcompany.com/programs/archery-coaching"
                value={formData.websiteRedirectUrl}
                onChange={(e) => setFormData((prev) => ({ ...prev, websiteRedirectUrl: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            {/* Description */}
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Description
              </label>
              <textarea
                rows="3"
                placeholder="Provide detailed description of this offering..."
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
              />
            </div>
          </div>
        </div>

        {/* Section 2: Images Upload (Cloudinary Integration) */}
        <div className="bg-white p-5 md:p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
            <div>
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-red-600" />
                Program Images (Max 5)
              </h2>
              <p className="text-xs text-gray-400">
                Upload to Cloudinary with secure Public ID tracking for easy deletion
              </p>
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1.5 text-xs font-semibold bg-red-50 text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-100 transition-colors"
            >
              <UploadCloud className="w-3.5 h-3.5" />
              <span>Select Images</span>
            </button>
            <input
              type="file"
              ref={fileInputRef}
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {/* Existing images from Cloudinary */}
            {existingImages.map((img) => (
              <div key={img.publicId} className="relative group h-24 rounded-lg overflow-hidden border border-gray-200">
                <img src={img.url} alt="Uploaded" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => handleRemoveExistingImage(img)}
                  className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-80 hover:opacity-100 transition-opacity"
                  title="Remove from Cloudinary"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}

            {/* Newly Selected Local Files */}
            {newFiles.map((file, idx) => (
              <div key={idx} className="relative group h-24 rounded-lg overflow-hidden border border-dashed border-red-300 bg-red-50/30">
                <img
                  src={URL.createObjectURL(file)}
                  alt="New preview"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveNewFile(idx)}
                  className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-80 hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>

          {existingImages.length === 0 && newFiles.length === 0 && (
            <p className="text-xs text-gray-400 italic text-center py-4">No images selected yet.</p>
          )}
        </div>

        {/* Section 3: Dynamic Pricing Plans with GST & Discount Calculation */}
        <div className="bg-white p-5 md:p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
            <div>
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <IndianRupee className="w-4 h-4 text-red-600" />
                Pricing Plans & Tax Configuration
              </h2>
              <p className="text-xs text-gray-400">
                Add one-time rates or multiple subscription plans (Monthly, Annual) with discounts and GST.
              </p>
            </div>
            <button
              type="button"
              onClick={handleAddPricingPlan}
              className="flex items-center gap-1.5 text-xs font-semibold bg-red-50 text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-100 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add Plan</span>
            </button>
          </div>

          <div className="space-y-4">
            {formData.pricingPlans.map((plan, idx) => {
              const fin = computePlanFinancials(plan);

              return (
                <div
                  key={idx}
                  className="p-4 border border-gray-200 rounded-xl bg-gray-50/50 hover:border-gray-300 transition-all"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold text-gray-700 uppercase">
                      Plan #{idx + 1}
                    </span>
                    {formData.pricingPlans.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemovePricingPlan(idx)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Remove Plan"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    {/* Plan Name */}
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
                        Plan Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Monthly, Annual"
                        value={plan.planName}
                        onChange={(e) => handlePlanChange(idx, 'planName', e.target.value)}
                        className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>

                    {/* Billing Cycle */}
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
                        Billing Cycle
                      </label>
                      <SelectDropdown
                        options={[
                          { value: 'one_time', label: 'One Time' },
                          { value: 'monthly', label: 'Monthly' },
                          { value: 'quarterly', label: 'Quarterly' },
                          { value: 'half_yearly', label: 'Half Yearly' },
                          { value: 'yearly', label: 'Yearly' },
                          { value: 'per_session', label: 'Per Session' },
                        ]}
                        value={plan.billingCycle}
                        onChange={(val) => handlePlanChange(idx, 'billingCycle', val)}
                        valueKey="value"
                        labelKey="label"
                        searchable={false}
                      />
                    </div>

                    {/* Original Price */}
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
                        Original Price (MRP) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        placeholder="e.g. 5000"
                        value={plan.originalPrice}
                        onChange={(e) => handlePlanChange(idx, 'originalPrice', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>

                    {/* Discount Type */}
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
                        Discount Type
                      </label>
                      <SelectDropdown
                        options={[
                          { value: 'none', label: 'No Discount' },
                          { value: 'percentage', label: 'Percentage (%)' },
                          { value: 'flat', label: 'Flat Cash (₹)' },
                        ]}
                        value={plan.discountType}
                        onChange={(val) => handlePlanChange(idx, 'discountType', val)}
                        valueKey="value"
                        labelKey="label"
                        searchable={false}
                      />
                    </div>

                    {/* Discount Value */}
                    {plan.discountType !== 'none' && (
                      <div>
                        <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
                          Discount Value ({plan.discountType === 'percentage' ? '%' : '₹'})
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={plan.discountValue}
                          onChange={(e) => handlePlanChange(idx, 'discountValue', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                    )}

                    {/* GST Rate */}
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
                        GST Rate (%)
                      </label>
                      <SelectDropdown
                        options={[
                          { value: 0, label: '0% (Exempted)' },
                          { value: 5, label: '5%' },
                          { value: 12, label: '12%' },
                          { value: 18, label: '18% (Standard)' },
                          { value: 28, label: '28%' },
                        ]}
                        value={plan.gstRate}
                        onChange={(val) => handlePlanChange(idx, 'gstRate', Number(val))}
                        valueKey="value"
                        labelKey="label"
                        searchable={false}
                      />
                    </div>

                    {/* Inclusive GST Toggle */}
                    <div className="flex items-center gap-2 pt-6">
                      <input
                        type="checkbox"
                        id={`inclusive_${idx}`}
                        checked={plan.isTaxInclusive}
                        onChange={(e) => handlePlanChange(idx, 'isTaxInclusive', e.target.checked)}
                        className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500 cursor-pointer"
                      />
                      <label htmlFor={`inclusive_${idx}`} className="text-xs font-medium text-gray-700 cursor-pointer">
                        Tax Inclusive Price
                      </label>
                    </div>
                  </div>

                  {/* Real-time Financial Preview Box */}
                  <div className="mt-3 p-3 bg-white border border-gray-200 rounded-lg flex flex-wrap items-center justify-between text-xs gap-3">
                    <span className="text-gray-500">
                      Selling Price (After Discount): <strong className="text-gray-900">₹{fin.sellingPrice.toLocaleString('en-IN')}</strong>
                    </span>
                    <span className="text-gray-500">
                      Tax ({plan.gstRate}%): <strong className="text-gray-900">₹{fin.taxAmount.toLocaleString('en-IN')}</strong>
                    </span>
                    <span className="text-emerald-700 font-bold text-sm bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-200">
                      Total Payable: ₹{fin.totalAmount.toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Section 4: Duration / Sessions & Physical Stock Details */}
        <div className="bg-white p-5 md:p-6 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-base font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">
            Delivery & Inventory Details
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Duration Value */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Duration Value
              </label>
              <input
                type="number"
                min="0"
                value={formData.duration.value}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  duration: { ...prev.duration, value: parseFloat(e.target.value) || 0 }
                }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            {/* Duration Unit */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Duration Unit
              </label>
              <SelectDropdown
                options={[
                  { value: 'na', label: 'Not Applicable' },
                  { value: 'hours', label: 'Hours' },
                  { value: 'days', label: 'Days' },
                  { value: 'weeks', label: 'Weeks' },
                  { value: 'months', label: 'Months' },
                  { value: 'years', label: 'Years' },
                  { value: 'sessions', label: 'Sessions' },
                ]}
                value={formData.duration.unit}
                onChange={(val) => setFormData((prev) => ({
                  ...prev,
                  duration: { ...prev.duration, unit: val }
                }))}
                valueKey="value"
                labelKey="label"
                searchable={false}
              />
            </div>

            {/* Total Sessions */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Total Classes / Sessions
              </label>
              <input
                type="number"
                min="0"
                placeholder="e.g. 24 classes for archery"
                value={formData.totalSessions}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  totalSessions: parseInt(e.target.value) || 0
                }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            {/* Stock Quantity */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Stock Quantity
              </label>
              <input
                type="number"
                min="0"
                value={formData.stockQuantity}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  stockQuantity: parseInt(e.target.value) || 0
                }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>
        </div>

        {/* Section 5: Dynamic Custom Fields */}
        <div className="bg-white p-5 md:p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
            <div>
              <h2 className="text-base font-bold text-gray-900">Custom Attributes</h2>
              <p className="text-xs text-gray-400">
                Add division-specific attributes (e.g., Warranty, Batch Timings, Eligibility).
              </p>
            </div>
            <button
              type="button"
              onClick={handleAddCustomField}
              className="flex items-center gap-1.5 text-xs font-semibold bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add Attribute</span>
            </button>
          </div>

          {formData.customFields.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No custom attributes added yet.</p>
          ) : (
            <div className="space-y-3">
              {formData.customFields.map((field, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
                  <input
                    type="text"
                    placeholder="Label (e.g. Warranty)"
                    value={field.label}
                    onChange={(e) => handleCustomFieldChange(idx, 'label', e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                  <SelectDropdown
                    options={[
                      { value: 'text', label: 'Text' },
                      { value: 'number', label: 'Number' },
                      { value: 'boolean', label: 'Boolean (Yes/No)' },
                      { value: 'date', label: 'Date' },
                    ]}
                    value={field.fieldType}
                    onChange={(val) => handleCustomFieldChange(idx, 'fieldType', val)}
                    valueKey="value"
                    labelKey="label"
                    searchable={false}
                  />
                  <input
                    type="text"
                    placeholder="Value (e.g. 2 Years)"
                    value={field.value}
                    onChange={(e) => handleCustomFieldChange(idx, 'value', e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveCustomField(idx)}
                    className="text-red-500 hover:text-red-700 p-1 w-8"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section 6: Status Toggle & Form Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActiveToggle"
              checked={formData.isActive}
              onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
              className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500 cursor-pointer"
            />
            <label htmlFor="isActiveToggle" className="text-sm font-semibold text-gray-800 cursor-pointer select-none">
              Mark this Offering as Active
            </label>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => navigate(`/${portalName}/sales/settings/programs`)}
              className="w-full sm:w-auto px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitLoading}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-sm transition-colors disabled:opacity-60"
            >
              {submitLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{isEditMode ? 'Update Offering' : 'Create Offering'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProgramForm;